/**
 * DispatchTab — contractor assignment, contractor estimate, and contractor
 * work order for a lead (Phase 8f — V6 Dispatch).
 *
 * PRIVACY (V6 §1): this tab and every contractor-facing preview/document show
 * ONLY the contractor's own pay. They must never display the customer estimate
 * total, customer payments, or broker margin. The margin strip is broker-only
 * and lives on the Estimate tab (BillingSections), never here.
 *
 * The contractor estimate is B2B: no tax, no discounts. The broker types dollar
 * amounts directly (commit-on-blur, consistent with inventory 8e.1). total is
 * recomputed + cached server-side on every write.
 */

import React, { useMemo, useState } from 'react';
import { tintOf } from '../lib/theme';
import { numberInput } from '../lib/numberInput';
import { gate } from './States';
import { formatPhone } from '../lib/format';
import { useApi, useApiClient } from '../hooks/useApi';
import { useRowSync } from '../hooks/useRowSync';
import { useAction, useIntentKey } from '../hooks/useAction';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney, formatDate, today, formatDateTime, COMPANY_TZ } from '../lib/utils';
import { todayInTz } from '../lib/time';
import { isLongDistance } from '../lib/moveType';
import { calcContractorEstimate, contractorFinalOf } from '../lib/estimate';
import { Icons } from '../lib/icons';
import { formatDocNo } from '../lib/jobNumber';
import { buildDriverText } from '../lib/driverText';
import { ContractorLinesTable, C } from './ContractorLines';
import { HistoryButton, HistoryPopup, HistoryRow, changeFromText } from './HistoryPopup';

const FALLBACK_METHODS = ['Cash', 'Cheque', 'E-transfer', 'Credit card', 'Other'];

const STATUS_COLOR: Record<string, string> = {
  draft: C.muted,
  sent: C.accent,
  approved: C.green,
  rejected: C.red,
  void: C.dim,
};

interface DispatchTabProps {
  leadId: string;
  lead: any;
  onLeadChanged: () => void;
  /** User id -> display name, for "confirmed by". */
  repName: (id: string | null | undefined) => string | null;
  /** Contractor notes are edited on the Estimate tab. */
  onEditNotes: () => void;
}

export function DispatchTab({ leadId, lead, onLeadChanged, repName, onEditNotes }: DispatchTabProps) {
  const api = useApiClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: ce, loading: ceLoading, error: ceError, refetch: refetchCE, refresh: refreshCE } = useApi<{ assignment: any; estimate: any; items: any[] }>(
    async () => (await api.getContractorEstimate(leadId)).data!,
    { dependencies: [leadId] }
  );
  const { data: contractors, refresh: refreshContractors } = useApi<any[]>(
    async () => (await api.getContractors()).data || []
  );
  const { data: documents, refresh: refreshDocuments } = useApi<any[]>(
    async () => (await api.getDocuments(leadId)).data || [],
    { dependencies: [leadId] }
  );
  // Company fee catalog for "+ Add fee" (same catalog as the customer estimate).
  const { data: fees } = useApi<any[]>(async () => (await api.getFees()).data || []);
  // Contractor payments (admin-only accounting): THIS job's, to show paid /
  // outstanding — not every payment in the company, filtered here.
  const { data: allContractorPayments, refresh: refreshCPayments } = useApi<any[]>(
    async () => { try { return (await api.getContractorPayments(leadId)).data || []; } catch { return []; } },
    { skip: !isAdmin, dependencies: [leadId] }
  );
  const { data: methodRows } = useApi<any[]>(async () => {
    try { return (await api.getConfigList('payment_method')).data || []; } catch { return []; }
  });
  const paymentMethods = methodRows && methodRows.length > 0 ? methodRows.map((r) => r.value) : FALLBACK_METHODS;

  const assignment = ce?.assignment;
  const estimate = ce?.estimate ?? null;
  /**
   * NOTHING HERE BLANKS AFTER A SAVE. refetchCE() set `loading`, and the
   * dispatch gate answers loading by replacing the section — so every line edit
   * flashed the contractor estimate away. Every write below now refreshes
   * quietly (useApi.refresh); refetchCE is kept only for the gate's own retry.
   *
   * LINE EDITS AND REMOVES ARE ON SCREEN AT ONCE (useRowSync), saved ~600ms
   * after the last change per line. The total is computed here by
   * calcContractorEstimate from the lines on screen — no tax, no discounts, no
   * server-only maths — and is marked "Updating…" until the batch has saved and
   * the quiet refresh has brought the server's lines back.
   */
  const sync = useRowSync<any>({
    update: (id, patch) => api.updateContractorEstimateItem(id, patch),
    remove: (id) => api.deleteContractorEstimateItem(id),
    onSettled: refreshCE,
  });
  const items = sync.view(ce?.items ?? []);
  const lineState = new Map(items.map((it: any) => [it.id, it]));
  const keyOfLine = (id: string) => lineState.get(id)?._key ?? id;
  const status: string = estimate?.status ?? '';
  // A LOCAL approved estimate stays editable (§9.9): its lines are what is owed,
  // and each change goes straight to the books, audited. Long distance locks.
  const localApproved = status === 'approved' && !isLongDistance(lead);
  const editable = !!estimate && (status === 'draft' || status === 'sent' || status === 'rejected' || localApproved);

  // Compute via the shared engine — base weight line reads lead.weight_lbs live,
  // so a weight change recomputes without a write — UNTIL an invoice is issued:
  // then the estimate carries the invoice's final weight / hours (049) and prices
  // from those, exactly as the server's stored total does. NO tax, NO discounts.
  const final = contractorFinalOf(estimate);
  const calc = calcContractorEstimate(items as any[], lead, (ce as any)?.accessorials ?? [], final);
  const contractorTotal = calc.total;

  // Contractor payment progress (approved estimate). Paid = payments logged
  // against this lead's current estimate; outstanding = locked total − paid.
  const leadCPayments = useMemo(
    () => (allContractorPayments ?? []).filter((p) => p.lead_id === leadId && (!estimate || p.contractor_estimate_id === estimate.id)),
    [allContractorPayments, leadId, estimate]
  );
  const paidToContractor = leadCPayments.reduce((s, p) => s + Number(p.amount || 0), 0);

  // History popup (admins): the job's dispatch log lines and every contractor
  // payment on it. Fetched when opened; the lines no longer sit in the right-hand log.
  const { data: dispatchLog, error: dispatchLogError } = useApi<any[]>(
    async () => (await api.getActivityLog(leadId, 'dispatch')).data || [],
    { skip: !historyOpen || !isAdmin, dependencies: [leadId, historyOpen] }
  );
  const historyRows: HistoryRow[] | null = useMemo(() => {
    if (!dispatchLog) return null;
    const lines: HistoryRow[] = dispatchLog.map((a) => ({ id: `a:${a.id}`, at: a.created_at, text: a.description || '—', change: changeFromText(a.description) }));
    const pays: HistoryRow[] = (allContractorPayments ?? []).filter((p) => p.lead_id === leadId).map((p) => ({
      id: `p:${p.id}`, at: p.paid_on, text: 'Payment to contractor', note: [p.method, p.notes].filter(Boolean).join(' · ') || null, change: -Number(p.amount || 0),
    }));
    return [...lines, ...pays];
  }, [dispatchLog, allContractorPayments, leadId]);
  // The tip on the issued invoice is owed 100 % to the crew (§9.2): it is part
  // of what they are paid, so it is in "owed" here, as on the Finalize tab.
  const tipOwed = Number((ce as any)?.tipOwed) || 0;
  const owedTotal = Math.round((contractorTotal + tipOwed) * 100) / 100;
  const outstanding = Math.round((owedTotal - paidToContractor) * 100) / 100;
  const [payModalOpen, setPayModalOpen] = useState(false);

  // 062: client lines removed / added on the invoice that this contractor
  // estimate may need to follow. Never applied automatically.
  const { data: noticeData, refresh: refreshNotices } = useApi<{ draftId: string | null; notices: { key: string; text: string }[] }>(
    async () => { try { return (await api.getContractorNotices(leadId)).data ?? { draftId: null, notices: [] }; } catch { return { draftId: null, notices: [] }; } },
    { dependencies: [leadId] }
  );
  const invoiceNotices = noticeData?.notices ?? [];
  const markReviewed = () =>
    run(async () => {
      if (!noticeData?.draftId || !invoiceNotices.length) return;
      await api.markContractorReviewed(noticeData.draftId, invoiceNotices.map((n) => n.key));
      await refreshNotices();
    });
  const goToEstimate = () => document.getElementById('contractor-estimate')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const cwoDocs = useMemo(
    () => (documents ?? []).filter((d) => d.doc_type === 'contractor_work_order'),
    [documents]
  );

  // ---- Driver text ----------------------------------------------------------
  // Once the contractor estimate is approved (or a work order exists): a plain
  // text job sheet, contractor pay only, copied for pasting into a message.
  // Shown in a dialog too, so it can be read and selected on a phone.
  const driverTextReady = status === 'approved' || cwoDocs.some((d) => d.status !== 'void');
  const hourlyBase = calc.items.find((l) => l.source === 'base')?.rateKind === 'hourly';
  // Local hourly: the crew is on the client estimate's base line. Fetched ahead
  // so the copy runs inside the click (Safari refuses a clipboard write after an await).
  const { data: clientEstimate } = useApi<{ estimate: any; items: any[] }>(
    async () => (await api.getEstimate(leadId)).data!,
    { skip: !driverTextReady || !hourlyBase, dependencies: [leadId, driverTextReady, hourlyBase] }
  );
  const [driverText, setDriverText] = useState<string | null>(null);
  const [driverCopied, setDriverCopied] = useState(false);
  const copyDriverText = (text: string) => {
    setDriverCopied(false);
    navigator.clipboard?.writeText(text).then(
      () => {
        setDriverCopied(true);
        api.logDriverTextCopied(leadId).catch((e) => console.error('Driver text: activity line not written', e));
      },
      () => setDriverCopied(false) // not allowed here: the dialog shows the text to select by hand
    );
  };
  const openDriverText = () => {
    const b = (clientEstimate?.items ?? []).find((i: any) => i.source === 'base');
    const whole = (v: unknown) => (v != null && v !== '' && Number(v) > 0 ? Math.round(Number(v)) : null);
    const text = buildDriverText({ lead, calc, crew: b ? { movers: whole(b.crew_size), trucks: whole(b.trucks) } : null });
    setDriverText(text);
    copyDriverText(text);
  };

  // E3 contractor sends — INLINE feedback (never an alert), so a missing-email
  // message names the contractor and a failure is visible without a popup.
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null);
  // Contractor emails: one at a time, keyed (a repeat is not a second email).
  const sendGuard = useAction();
  const sendCE = (override = false) => sendGuard.run((key) => sendCENow(override, key));
  const sendCWO = (override = false) => sendGuard.run((key) => sendCWONow(override, key));
  const sendCENow = async (override: boolean, key: string) => {
    setBusy(true); setSendMsg(null);
    try {
      // The email carries the server's lines; send what is on screen first.
      if (!(await sync.flushAll())) throw new Error('A line did not save — retry it before sending.');
      await api.withIdempotencyKey(key).sendContractorEstimate(leadId, override);
      await refreshCE();
      setSendMsg({ ok: true, text: 'Contractor estimate emailed to the contractor.' });
    } catch (e: any) {
      if (e?.code === 'SUPPRESSED') setSendMsg({ ok: false, text: e.message, onOverride: () => sendCE(true) });
      else setSendMsg({ ok: false, text: e?.message || 'Send failed — please try again.' });
    } finally { setBusy(false); }
  };
  const sendCWONow = async (override: boolean, key: string) => {
    setBusy(true); setSendMsg(null);
    try {
      await api.withIdempotencyKey(key).sendContractorWorkOrder(leadId, override);
      await refreshDocuments();
      setSendMsg({ ok: true, text: 'Contractor work order emailed to the contractor.' });
    } catch (e: any) {
      if (e?.code === 'SUPPRESSED') setSendMsg({ ok: false, text: e.message, onOverride: () => sendCWO(true) });
      else setSendMsg({ ok: false, text: e?.message || 'Send failed — please try again.' });
    } finally { setBusy(false); }
  };

  // ONE DISPATCH ACTION AT A TIME (useAction): a double click on "+ Add fee",
  // Generate or Save payment no longer runs it twice. Creates carry a key.
  const guard = useAction();
  const run = (fn: (key: string) => Promise<void>) =>
    guard.run(async (key) => {
      setBusy(true);
      try {
        await fn(key);
      } catch (e: any) {
        console.error('Dispatch action failed:', e);
        window.alert(e?.message || 'Action failed');
      } finally {
        setBusy(false);
      }
    });

  // ---- Contractor minimum weight (MW2) ------------------------------------
  // Editing the value marks it overridden (mirror stops); reset re-mirrors the
  // client minimum. Never writes weight_lbs — the floor is compute/display only.
  const saveContractorMin = (patch: Record<string, any>) =>
    run(async () => {
      await api.updateLead(leadId, patch);
      onLeadChanged();
    });

  // ---- Assignment ---------------------------------------------------------
  const [showAdd, setShowAdd] = useState(false);

  const assign = (contractorId: string | null) =>
    run(async () => {
      await api.assignContractor(leadId, contractorId);
      await refreshCE();
      onLeadChanged();
    });

  const createAndAssign = (form: any) =>
    run(async () => {
      const created = (await api.createContractor(form)).data;
      await refreshContractors();
      if (created?.id) await api.assignContractor(leadId, created.id);
      setShowAdd(false);
      await refreshCE();
      onLeadChanged();
    });

  // ---- Estimate lifecycle -------------------------------------------------
  // Generate is the ONLY creation action (8f.1): it seeds a single move-type
  // base line (weight or hourly, rate 0) — it does NOT copy the customer estimate.
  const generate = () =>
    run(async () => {
      await api.generateContractorEstimate(leadId);
      await refreshCE();
    });

  const commitField = (itemId: string, patch: Record<string, any>) => sync.edit(keyOfLine(itemId), patch);

  const addFee = (feeId: string) =>
    run(async (key) => {
      if (!estimate || !feeId) return;
      const payload = { contractor_estimate_id: estimate.id, fee_id: feeId };
      const keyed = api.withIdempotencyKey(key);
      try {
        await keyed.addContractorEstimateItem(payload);
      } catch (e: any) {
        // A1 duplicate guard — warn, never block (see LeadDetailPage.addFee).
        if (e?.code !== 'DUPLICATE_ACCESSORIAL') throw e;
        if (!window.confirm(e.message)) return;
        await keyed.addContractorEstimateItem(payload, true);
      }
      await refreshCE();
    });

  const addCustom = () =>
    run(async (key) => {
      if (!estimate) return;
      await api.withIdempotencyKey(key).addContractorEstimateItem({ contractor_estimate_id: estimate.id, description: 'Custom line', amount: 0 });
      await refreshCE();
    });

  const removeLine = (itemId: string) => sync.remove(keyOfLine(itemId));

  // §9.3: local hourly, not approved — rewrite labour, fees and hand-added access
  // lines from the client's figures. Custom lines stay.
  const [confirmDefaults, setConfirmDefaults] = useState(false);
  const applyDefaults = () =>
    run(async () => {
      if (!(await sync.flushAll())) throw new Error('A line did not save — retry it first.');
      await api.applyContractorLocalDefaults(leadId);
      setConfirmDefaults(false);
      await refreshCE();
    });

  const changeStatus = (action: string) =>
    run(async () => {
      // Approving locks the amounts, so it must lock the ones on screen: send
      // any line edit still waiting first, and stop if one did not save.
      if (!(await sync.flushAll())) throw new Error('A line did not save — retry it before changing the status.');
      if (action === 'void' && !window.confirm('Void this contractor estimate?')) return;
      // A draft approved without sending (agreed with the contractor by phone).
      if (action === 'approved' && status === 'draft') {
        if (!window.confirm(`Approve without sending the estimate to ${assignment?.contractor?.name ?? 'the contractor'}?`)) return;
      } else if (action === 'approved' && !window.confirm(isLongDistance(lead)
        ? 'Mark this contractor estimate approved? This locks the amounts and unlocks the contractor work order.'
        : 'Mark this contractor estimate approved? It becomes what the contractor is owed and unlocks the contractor work order.')) return;
      let reason: string | undefined;
      if (action === 'rejected') {
        const r = window.prompt('Rejection reason (optional):', '');
        if (r === null) return; // cancelled
        reason = r;
      }
      await api.updateContractorEstimateStatus(leadId, action, reason);
      await refreshCE();
    });

  // The modal's key: one opening of "Log payment" is one payment.
  const logContractorPayment = (data: any, key: string) =>
    run(async () => {
      await api.withIdempotencyKey(key).addContractorPayment(leadId, {
        contractor_estimate_id: estimate?.id ?? null,
        contractor_id: assignment?.contractor_id ?? null,
        amount: data.amount,
        paid_on: data.paid_on,
        method: data.method || null,
        notes: data.notes || null,
      });
      setPayModalOpen(false);
      await refreshCPayments();
      await refreshCE(); // obligation changed
      onLeadChanged();
    });

  // ---- Contractor work order ----------------------------------------------
  const generateCWO = () =>
    run(async (key) => {
      await api.withIdempotencyKey(key).generateContractorWorkOrder(leadId);
      await refreshDocuments();
    });

  const viewDoc = (doc: any) => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(doc.rendered_content || '<p>No content.</p>');
    w.document.close();
  };

  // Client-side print preview of the contractor estimate (privacy-safe: it
  // shows only the contractor's own line amounts + total, never customer data).
  const printPreview = () => {
    const contractor = assignment?.contractor;
    const rows = calc.items
      .map(
        (l) =>
          `<tr><td>${escapeHtml(l.desc)}</td><td style="text-align:right;font-family:monospace">${formatMoney(l.net)}</td></tr>`
      )
      .join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Contractor Estimate</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1f2b;padding:32px;max-width:720px;margin:0 auto}
      h1{color:#3b82f6;text-transform:uppercase;font-size:22px;letter-spacing:1px}
      .muted{color:#6b7280;font-size:13px} table{width:100%;border-collapse:collapse;margin-top:16px;font-size:14px}
      th{text-align:left;color:#9ca3af;font-size:11px;text-transform:uppercase;border-bottom:2px solid #e5e7eb;padding:8px 6px}
      td{padding:8px 6px;border-bottom:1px solid #eef0f3} .grand{font-weight:800;font-size:18px;border-top:2px solid #111827}
      </style></head><body>
      <h1>Contractor Estimate</h1>
      <div class="muted"><b>${escapeHtml(contractor?.name ?? '—')}</b> · Status: ${escapeHtml(status)}</div>
      <div class="muted">Job: ${escapeHtml(lead.move_type ?? '')} ${lead.move_size ? '· ' + escapeHtml(lead.move_size) : ''} · Move date: ${formatDate(lead.move_date) || '—'}</div>
      <table><thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="2" class="muted">No line items.</td></tr>'}</tbody>
      <tfoot><tr class="grand"><td>Total contractor pay</td><td style="text-align:right;font-family:monospace">${formatMoney(contractorTotal)}</td></tr></tfoot>
      </table></body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // ---- Render -------------------------------------------------------------

  // "No contractor assigned" is the normal state of a new job and was also what a
  // failed request looked like — on the tab a broker uses to decide whether a job
  // is covered. Contractors, fees and payment methods are lookups that degrade;
  // only the assignment itself gates.
  const dispatchGate = gate('Dispatch', ceLoading, ceError, !!ce, refetchCE);

  return (
    <div>
      {dispatchGate}
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <HistoryButton onClick={() => setHistoryOpen(true)} />
        </div>
      )}
      {historyOpen && (
        <HistoryPopup title="Dispatch history" onClose={() => setHistoryOpen(false)}
          sub="Contractor assignment, pay changes, approvals, weight sync and payments to the contractor. A payment shows as a reduction in what is owed."
          rows={historyRows} error={dispatchLogError} />
      )}
      {invoiceNotices.length > 0 && (
        <div style={{ fontSize: '12px', color: C.amber, background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, borderRadius: '8px', padding: '8px 10px', marginBottom: '12px' }}>
          {invoiceNotices.map((n) => <div key={n.key} style={{ marginBottom: '3px' }}>{n.text}</div>)}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
            <button onClick={goToEstimate} style={{ background: 'none', border: 'none', padding: 0, color: C.accent, cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>Go to the contractor estimate</button>
            <span style={{ color: C.dim, fontSize: '11px' }}>Nothing on it changed automatically.</span>
            <button onClick={markReviewed} disabled={busy} style={{ ...btn(C.panel2, C.amber), marginLeft: 'auto' }}>Mark reviewed</button>
          </div>
        </div>
      )}
      {/* ============ CONTRACTOR | JOB STATUS · NOTES | MINIMUM WEIGHT ============
          Two rows of pairs; each pair wraps to one column on a narrow screen. */}
      <div style={pairs}>
        <PairCell label="Contractor">
          <div style={{ ...panelStyle, flex: 1 }}>
            {assignment?.contractor ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontSize: '14px', color: C.text, fontWeight: 600 }}>{assignment.contractor.name}</div>
                  <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                    {[assignment.contractor.contact_name, formatPhone(assignment.contractor.phone), assignment.contractor.email].filter(Boolean).join(' · ') || 'No contact on file'}
                  </div>
                  {assignment.assigned_at && (
                    <div style={{ fontSize: '11px', color: C.dim, marginTop: '2px', fontFamily: C.mono }}>Assigned {formatDateTime(assignment.assigned_at)}</div>
                  )}
                </div>
                <button onClick={() => assign(null)} disabled={busy} style={btn(C.panel2, C.muted)}>Unassign</button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    defaultValue=""
                    onChange={(e) => e.target.value && assign(e.target.value)}
                    disabled={busy}
                    style={{ padding: '8px 10px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '12px', minWidth: '220px' }}
                  >
                    <option value="">Select contractor…</option>
                    {(contractors ?? []).filter((k) => k.active).map((k) => (
                      <option key={k.id} value={k.id}>{k.name}</option>
                    ))}
                  </select>
                  <button onClick={() => setShowAdd((s) => !s)} disabled={busy} style={btn(C.panel2, C.accent)}>
                    {showAdd ? 'Cancel' : '+ Add new'}
                  </button>
                </div>
                {showAdd && <AddContractorForm onSubmit={createAndAssign} busy={busy} />}
              </div>
            )}
          </div>
        </PairCell>
        <PairCell label="Job Status">
          <JobStatusBox lead={lead} repName={repName} onSaved={onLeadChanged} />
        </PairCell>
      </div>
      <div style={{ ...pairs, marginTop: '14px' }}>
        <PairCell label="Contractor Notes">
          <div style={{ ...panelStyle, flex: 1 }}>
            <div style={{ fontSize: '12px', color: lead.contractor_notes ? C.text : C.dim, whiteSpace: 'pre-wrap', minHeight: '20px' }}>
              {lead.contractor_notes || 'No contractor notes yet.'}
            </div>
            <button onClick={onEditNotes} style={{ marginTop: '10px', background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: '11px', padding: 0 }}>
              Edit on Estimate tab →
            </button>
          </div>
        </PairCell>
        <PairCell label="Contractor Minimum Weight">
          <ContractorMinWeightBox lead={lead} busy={busy} onSave={saveContractorMin} />
        </PairCell>
      </div>

      {/* ============ CONTRACTOR ESTIMATE ============ */}
      <div id="contractor-estimate" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 12px', scrollMarginTop: '12px' }}>
        <h3 style={sectionH3}>Contractor Estimate</h3>
        <span style={{ flex: 1 }} />
        {driverTextReady && (
          <button onClick={openDriverText} style={{ ...btn(C.panel2, C.accent), marginRight: '10px' }}>Copy text for driver</button>
        )}
        {estimate && (
          <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '999px', background: `${tintOf(STATUS_COLOR[status], 13)}`, color: STATUS_COLOR[status], textTransform: 'uppercase', fontWeight: 700 }}>
            {status}
          </span>
        )}
      </div>

      {/* 049: the basis the contractor is paid on, once an invoice set it, and a
          reprice waiting for an admin (decided on the Finalize tab). */}
      {final && (
        <div style={{ fontSize: '11px', color: C.muted, margin: '8px 0' }}>
          Priced at the invoice's final {final.weightLbs != null ? `${Number(final.weightLbs).toLocaleString()} lb` : `${final.hours} h${final.crewSize ? `, crew ${final.crewSize}` : ''}`} — the lead's own weight no longer changes this estimate.
        </div>
      )}
      {(ce as any)?.pendingReprice && (
        <div style={{ fontSize: '12px', color: C.amber, background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, borderRadius: '8px', padding: '8px 10px', margin: '8px 0' }}>
          The issued invoice would change this contractor's pay from ${Number((ce as any).pendingReprice.old_total).toFixed(2)} to ${Number((ce as any).pendingReprice.new_total).toFixed(2)}. Accept or keep it on the Finalize tab.
        </div>
      )}


      {!assignment?.contractor ? (
        <div style={{ ...panelStyle, color: C.dim, fontSize: '12px' }}>Assign a contractor to generate their estimate.</div>
      ) : !estimate || status === 'void' ? (
        <div style={{ ...panelStyle, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', fontSize: '12px', color: C.dim }}>
            {status === 'void'
              ? 'This contractor estimate was voided. Generate a fresh one — it seeds a move-type base line the broker prices.'
              : 'No contractor estimate yet. Generate one — it seeds a single base line by move type (weight or hourly) at rate $0; the broker fills the rate and adds fees.'}
          </div>
          <button onClick={generate} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>
            {status === 'void' ? 'Regenerate' : 'Generate contractor estimate'}
          </button>
        </div>
      ) : (
        <>
          <ContractorLinesTable
            calc={calc}
            rowOf={(id) => lineState.get(id)}
            editable={editable}
            busy={busy}
            lead={lead}
            final={final}
            total={contractorTotal}
            saving={sync.busy}
            failed={sync.failed}
            onCommit={commitField}
            onRemove={removeLine}
            onRetry={(key) => sync.retry(key)}
          />

          {/* Estimate actions */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            {editable && (
              <>
                <select
                  value=""
                  onChange={(e) => { const v = e.target.value; e.target.value = ''; if (v) addFee(v); }}
                  disabled={busy}
                  style={{ padding: '7px 10px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '12px' }}
                >
                  <option value="">+ Add fee…</option>
                  {(fees ?? []).map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <button onClick={addCustom} disabled={busy} style={btn(C.panel2, C.text)}>+ Add custom line</button>
                {(ce as any)?.canApplyLocalDefaults && (
                  confirmDefaults ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.muted }}>
                      Replace labour and fee lines with the local defaults? Custom lines stay.
                      <button onClick={applyDefaults} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Apply</button>
                      <button onClick={() => setConfirmDefaults(false)} disabled={busy} style={btn(C.panel2, C.muted)}>Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmDefaults(true)} disabled={busy} style={btn(C.panel2, C.accent)} title="Labour at the tariff's contractor %, fuel and materials 100%, extra truck at its contractor amount, admin 0%">Apply local defaults</button>
                  )
                )}
              </>
            )}
            <button onClick={printPreview} disabled={busy} style={btn(C.panel2, C.muted)}>Print preview</button>

            {status === 'draft' && (
              <>
                <button onClick={() => sendCE()} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Send to contractor</button>
                {/* Agreed by phone: skip the send and go straight to the work order. */}
                <button onClick={() => changeStatus('approved')} disabled={busy || !assignment?.contractor}
                  title={assignment?.contractor ? 'Approved by the contractor without sending them the estimate' : 'Assign a contractor first'}
                  style={btn(C.panel2, C.green)}>Approve (recorded internally)</button>
              </>
            )}
            {status === 'sent' && (
              <>
                <button onClick={() => sendCE()} disabled={busy} style={btn(C.panel2, C.accent)}>Re-send</button>
                <button onClick={() => changeStatus('approved')} disabled={busy} style={btn(C.greenFill, 'var(--crm-on-green)')}>Mark approved</button>
                <button onClick={() => changeStatus('rejected')} disabled={busy} style={btn(C.panel2, C.red)}>Reject</button>
              </>
            )}
            {status === 'rejected' && (
              <button onClick={() => changeStatus('edit-resend')} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Edit &amp; re-send</button>
            )}
            {status !== 'void' && (
              <button onClick={() => changeStatus('void')} disabled={busy} style={btn(C.panel2, C.red)}>Void</button>
            )}
          </div>

          {status === 'rejected' && estimate.rejection_reason && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: C.red }}>Rejection reason: {estimate.rejection_reason}</div>
          )}
          {status === 'approved' && (
            <div style={{ marginTop: '10px', fontSize: '11px', color: C.dim }}>
              {localApproved
                ? 'Approved — a change here changes what the contractor is owed and is recorded in the accounting log.'
                : 'Amounts locked. Void to make changes.'}
            </div>
          )}
        </>
      )}

      {/* Payment and work order side by side once approved (both show then). */}
      {status === 'approved' && (
      <div style={{ ...pairs, marginTop: '26px' }}>
      {/* ============ PAYMENT TO CONTRACTOR (post-approval, admin-only) ============ */}
      {isAdmin && (
        <PairCell label="Payment to Contractor">
          <div style={{ ...panelStyle, flex: 1 }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, auto))', gap: '20px', flex: 1 }}>
                <div>
                  <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase' }}>Estimate total</div>
                  <div style={{ fontFamily: C.mono, fontSize: '16px', color: C.text }}>{formatMoney(contractorTotal)}</div>
                </div>
                {tipOwed > 0 && (
                  <div>
                    <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase' }}>+ Tip (100% crew)</div>
                    <div style={{ fontFamily: C.mono, fontSize: '16px', color: C.text }}>{formatMoney(tipOwed)}</div>
                  </div>
                )}
                {tipOwed > 0 && (
                  <div>
                    <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase' }}>Total owed</div>
                    <div style={{ fontFamily: C.mono, fontSize: '16px', color: C.text, fontWeight: 700 }}>{formatMoney(owedTotal)}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase' }}>Paid to date</div>
                  <div style={{ fontFamily: C.mono, fontSize: '16px', color: C.green }}>{formatMoney(paidToContractor)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: C.muted, textTransform: 'uppercase' }}>Outstanding</div>
                  <div style={{ fontFamily: C.mono, fontSize: '16px', color: outstanding <= 0 ? C.green : C.amber }}>
                    {outstanding <= 0 ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icons.check size={13} /> Fully paid</span> : formatMoney(outstanding)}
                  </div>
                </div>
              </div>
              <button onClick={() => setPayModalOpen(true)} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>+ Log payment</button>
            </div>
            {leadCPayments.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '11px', color: C.dim }}>
                {leadCPayments.length} payment{leadCPayments.length === 1 ? '' : 's'} — listed in{' '}
                <button onClick={() => setHistoryOpen(true)} style={{ background: 'none', border: 'none', padding: 0, color: C.accent, cursor: 'pointer', fontSize: '11px' }}>History</button>.
              </div>
            )}
          </div>
        </PairCell>
      )}

      {/* ============ CONTRACTOR WORK ORDER (post-approval) ============ */}
      <PairCell label="Contractor Work Order">
        <div style={{ ...panelStyle, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: cwoDocs.length ? '12px' : 0 }}>
            <div style={{ flex: 1, minWidth: '200px', fontSize: '12px', color: C.muted }}>
              Generates a printable work order for the contractor showing their pay and job logistics only.
            </div>
            <button onClick={generateCWO} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Generate contractor work order</button>
          </div>
          {cwoDocs.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontFamily: C.mono, fontSize: '12px', color: d.status === 'void' ? C.dim : C.text }}>{formatDocNo(d.number)}</span>
              <span style={{ fontSize: '11px', color: C.dim }}>{formatDateTime(d.generated_at)}{d.status === 'sent' ? ' · sent' : ''}</span>
              <button onClick={() => viewDoc(d)} style={{ ...btn(C.panel2, C.accent), marginLeft: 'auto' }}>View / print</button>
              {/* Newest WO is the one the send targets. */}
              {i === 0 && d.status !== 'void' && (
                <button onClick={() => sendCWO()} disabled={busy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>{d.status === 'draft' ? 'Send to contractor' : 'Re-send'}</button>
              )}
            </div>
          ))}
        </div>
      </PairCell>
      </div>
      )}

      {driverText != null && (
        <DriverTextDialog text={driverText} copied={driverCopied} onCopy={() => copyDriverText(driverText)} onClose={() => setDriverText(null)} />
      )}

      {payModalOpen && (
        <LogPaymentModal
          defaultAmount={Math.max(0, outstanding)}
          methods={paymentMethods}
          busy={busy}
          onClose={() => setPayModalOpen(false)}
          onSave={logContractorPayment}
        />
      )}

      {sendMsg && (
        <div style={{ marginTop: '10px', fontSize: '12px', color: sendMsg.ok ? C.green : C.red, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>{sendMsg.text}</span>
          {sendMsg.onOverride && (
            <button onClick={sendMsg.onOverride} disabled={busy} style={{ padding: '3px 10px', background: C.panel2, border: `1px solid ${C.amberLine}`, color: C.amber, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Send anyway</button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Job status (Job confirmed) ----------------------------------------------
/**
 * JOB CONFIRMED = "contractor and final pickup date are confirmed with the
 * client". Ticking it asks for that date (pre-filled with the move date) and
 * saves both in one request; the calendar then shows the job on that one day
 * instead of the pickup window. Unticking keeps the date.
 *
 * Gated as the server gates it (routes/leads.ts): stage Booked or Invoiced, and
 * a contractor on the job. Dates stay 'YYYY-MM-DD' strings end to end, never a
 * Date, so the day saved is the day picked.
 */
function JobStatusBox({ lead, repName, onSaved }: { lead: any; repName: (id: string | null | undefined) => string | null; onSaved: () => void }) {
  const api = useApiClient();
  const [asking, setAsking] = useState(false);
  const [day, setDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const confirmed = !!lead.job_confirmed;
  const stageOk = lead.stage === 'Booked' || lead.stage === 'Invoiced';
  const blocked = !confirmed && (!lead.contractor_id ? 'Assign a contractor first' : !stageOk ? 'Requires stage “Booked” or “Invoiced”' : '');

  const moveDay = lead.move_date ? String(lead.move_date).slice(0, 10) : '';
  const start = lead.pickup_start ? String(lead.pickup_start).slice(0, 10) : '';
  const end = lead.pickup_end ? String(lead.pickup_end).slice(0, 10) : '';
  const outsideWindow = isLongDistance(lead) && !!day && ((!!start && day < start) || (!!end && day > end));

  const save = async (patch: Record<string, any>) => {
    setSaving(true); setErr('');
    try {
      await api.updateLead(lead.id, patch);
      setAsking(false);
      onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Could not save — please try again.');
    } finally { setSaving(false); }
  };

  const onToggle = (checked: boolean) => {
    setErr('');
    if (checked) { setDay(moveDay); setAsking(true); }
    else save({ job_confirmed: false });
  };

  // job_confirmed_at is an instant; the day it fell on is read in company time.
  const confirmedOn = lead.job_confirmed_at ? formatDate(todayInTz(COMPANY_TZ, new Date(lead.job_confirmed_at))) : '—';

  return (
    <div style={{ ...panelStyle, flex: 1 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.5 : 1 }}>
        <input
          type="checkbox"
          checked={confirmed || asking}
          disabled={!!blocked || saving || asking}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <span style={{ fontSize: '13px', color: C.text, fontWeight: 600 }}>Job confirmed</span>
        {blocked && <span style={{ fontSize: '11px', color: C.muted }}>{blocked}</span>}
      </label>
      <div style={{ fontSize: '11px', color: C.dim, marginTop: '6px' }}>
        Contractor and final pickup date confirmed with client. Shows as a single day on the calendar.
      </div>

      {asking && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${C.border}` }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Confirmed pickup date</span>
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              style={{ width: '170px', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' }}
            />
          </label>
          {outsideWindow && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: C.amber }}>
              Outside the pickup window ({formatDate(start || end)}{start && end ? ` – ${formatDate(end)}` : ''}). You can still confirm.
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button
              onClick={() => save({ job_confirmed: true, move_date: day })}
              disabled={saving || !day}
              style={{ ...btn(C.greenFill, 'var(--crm-on-green)'), opacity: saving || !day ? 0.5 : 1 }}
            >
              {saving ? 'Saving…' : 'Confirm'}
            </button>
            <button onClick={() => { setAsking(false); setErr(''); }} disabled={saving} style={btn(C.panel2, C.muted)}>Cancel</button>
          </div>
        </div>
      )}

      {err && <div style={{ marginTop: '10px', fontSize: '12px', color: C.red }}>{err}</div>}

      {confirmed && !asking && (
        <div style={{ marginTop: '10px', fontSize: '12px', color: C.green, display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Icons.check size={13} /> Confirmed for {formatDate(moveDay || null)} by {repName(lead.job_confirmed_by) ?? '—'} on {confirmedOn}
        </div>
      )}
    </div>
  );
}

// ---- Inline add-contractor form --------------------------------------------

function AddContractorForm({ onSubmit, busy }: { onSubmit: (form: any) => void; busy: boolean }) {
  const [form, setForm] = useState({ name: '', contact_name: '', email: '', phone: '' });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const input = { padding: '7px 10px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '12px' } as React.CSSProperties;
  return (
    <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', alignItems: 'end' }}>
      <input placeholder="Company / name *" value={form.name} onChange={(e) => set('name', e.target.value)} style={input} />
      <input placeholder="Contact name" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} style={input} />
      <input placeholder="Email" value={form.email} onChange={(e) => set('email', e.target.value)} style={input} />
      <input placeholder="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} style={input} />
      <button
        onClick={() => form.name.trim() && onSubmit(form)}
        disabled={busy || !form.name.trim()}
        style={{ ...btn(C.accentFill, 'var(--crm-on-fill)'), opacity: form.name.trim() ? 1 : 0.5 }}
      >
        Add &amp; assign
      </button>
    </div>
  );
}

// ---- Log-payment modal (contractor payment) --------------------------------

/** The driver text, readable and selectable on a phone; Copy again if the first copy was refused. */
function DriverTextDialog({ text, copied, onCopy, onClose }: { text: string; copied: boolean; onCopy: () => void; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', width: '420px', maxWidth: '92vw', maxHeight: '90dvh', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.text, flex: 1 }}>Text for driver</div>
          {copied
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: C.green }}><Icons.check size={13} /> Copied</span>
            : <span style={{ fontSize: '11px', color: C.muted }}>Not copied — select the text, or press Copy</span>}
        </div>
        <textarea readOnly value={text} onFocus={(e) => e.target.select()}
          style={{ flex: 1, minHeight: '320px', resize: 'none', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '8px', color: C.text, fontSize: '13px', lineHeight: 1.45, padding: '10px', fontFamily: 'inherit' }} />
        <div style={{ fontSize: '11px', color: C.dim }}>Contractor pay only. Nothing is sent.</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btn(C.panel2, C.muted)}>Close</button>
          <button onClick={onCopy} style={btn(C.accentFill, 'var(--crm-on-fill)')}>Copy</button>
        </div>
      </div>
    </div>
  );
}

function LogPaymentModal({ defaultAmount, methods, busy, onClose, onSave }: {
  defaultAmount: number; methods: string[]; busy: boolean; onClose: () => void; onSave: (d: any, idempotencyKey: string) => void;
}) {
  const intentKey = useIntentKey();
  const [amount, setAmount] = useState<string>(defaultAmount > 0 ? String(defaultAmount) : '');
  const [method, setMethod] = useState<string>(methods[0] ?? '');
  const [paidOn, setPaidOn] = useState<string>(today());
  const [notes, setNotes] = useState<string>('');
  const input: React.CSSProperties = { width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px' };
  const amt = Math.max(0, Number(amount) || 0);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '20px', width: '360px', maxWidth: '92vw' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: C.text }}>Log payment to contractor</div>
        <div style={{ display: 'grid', gap: '10px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Date</span>
            <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} style={input} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Amount</span>
            <input {...numberInput(1)} value={amount} onChange={(e) => setAmount(e.target.value)} style={input} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Method</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)} style={input}>
              {methods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} style={input} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={btn(C.panel2, C.muted)}>Cancel</button>
          <button onClick={() => onSave({ amount: amt, method, paid_on: paidOn, notes }, intentKey)} disabled={busy || amt <= 0} style={{ ...btn(C.accentFill, 'var(--crm-on-fill)'), opacity: busy || amt <= 0 ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Save payment'}</button>
        </div>
      </div>
    </div>
  );
}

// Contractor minimum weight (MW2): mirrors the client minimum until edited.
// Editing sets the override flag; "reset to match client" clears it and re-mirrors.
function ContractorMinWeightBox({ lead, busy, onSave }: { lead: any; busy: boolean; onSave: (patch: Record<string, any>) => void }) {
  const clientMin = Number(lead.minimum_weight ?? 2000);
  const overridden = !!lead.contractor_min_overridden;
  // When not overridden, show the mirrored client value (never blank).
  const current = overridden ? Number(lead.contractor_minimum_weight ?? clientMin) : clientMin;
  const [draft, setDraft] = useState<string>(String(current));
  React.useEffect(() => setDraft(String(current)), [current]);

  const commit = () => {
    const v = parseInt(draft) || 0;
    if (v === current) return;
    onSave({ contractor_minimum_weight: v, contractor_min_overridden: true });
  };
  const reset = () => onSave({ contractor_minimum_weight: clientMin, contractor_min_overridden: false });

  return (
    <div style={{ ...panelStyle, flex: 1, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <div style={{ minWidth: '180px' }}>
        <label style={{ fontSize: '9px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
          Pounds
          {!overridden && <span style={{ color: C.amber, marginLeft: '6px', textTransform: 'none', fontWeight: 500 }}>· matches client</span>}
          {overridden && <span style={{ color: C.muted, marginLeft: '6px', textTransform: 'none', fontWeight: 500 }}>· overridden</span>}
        </label>
        <input
          {...numberInput(1)}
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          style={{ width: '120px', background: 'transparent', border: 'none', color: C.text, fontSize: '13px', outline: 'none', padding: 0, fontFamily: C.mono }}
        />
      </div>
      {overridden && (
        <button onClick={reset} disabled={busy} style={{ background: 'none', border: 'none', color: C.accent, cursor: busy ? 'default' : 'pointer', fontSize: '11px', padding: 0 }}>
          Reset to match client ({clientMin.toLocaleString()})
        </button>
      )}
    </div>
  );
}


// ---- Small style helpers ---------------------------------------------------

const panelStyle: React.CSSProperties = { background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' };
/** Two boxes side by side, one column when narrow (the address cards' auto-fit). */
const pairs: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '11px' };
const sectionH3: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', margin: 0 };
const td: React.CSSProperties = { padding: '7px 12px', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.panel2}` };
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: C.muted };

function btn(bg: string, color: string): React.CSSProperties {
  return { padding: '7px 13px', background: bg, color, border: `1px solid ${bg === C.panel2 ? C.border : bg}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
}

/** A labelled box in a pair; the box stretches so both in a row are the same height. */
function PairCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <h3 style={{ ...sectionH3, marginBottom: '12px', ...style }}>{children}</h3>;
}

function escapeHtml(v: unknown): string {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
